import React, { useState, useEffect, useRef } from "react";
import { Link, Redirect, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { Button, message, Spin, Alert, Modal, Form, Input, Select, Card, Result } from "antd";
import logo from "@iso/assets/images/logo.png";
import queryString from "query-string";
import { propertyCheckinService } from "@iso/services";
import VisitorFormModal from "@iso/containers/Pages/VisitorForm/VisitorFormModal";
import PropertyCheckinLandingWrapper from "./PropertyCheckinLanding.styles";

const SUCCESS_CODE = "0000";

// "I am a Contractor" on Android opens the Contractor app with the same link as
// the Contractor form QR (/contractor-form?token=...), so both ways in behave the
// same. Chrome never hands a same-site link to an app, hence the intent: URL.
// When the app is missing or cannot handle the link, Chrome loads
// browser_fallback_url: this landing with role=contractor, which carries on with
// the contractor check-in here. iOS stays in the browser: Safari also never
// hands a same-domain link to an app. Skipped inside an Android WebView (a
// scanner app's built-in browser, "; wv)" in the user agent): only Chrome and
// Samsung Internet honour browser_fallback_url, so a WebView shows an error page.
const CONTRACTOR_ANDROID_PACKAGE = "com.contractor";
const isAndroid = () => {
  const ua = navigator.userAgent || "";
  return /Android/i.test(ua) && !/; wv\)/.test(ua);
};
const openContractorApp = (contractorFormUrl, token, divisionId) => {
  const target = new URL(contractorFormUrl);
  const query = queryString.stringify({ token, role: "contractor", division: divisionId || undefined });
  const fallback = `${window.location.origin}${window.location.pathname}?${query}`;
  window.location.href =
    `intent://${target.host}${target.pathname}${target.search}#Intent;scheme=https;` +
    `package=${CONTRACTOR_ANDROID_PACKAGE};S.browser_fallback_url=${encodeURIComponent(fallback)};end`;
};

// Contractor self-checkin session is remembered in the guest's own browser for 12h
// so they can later check out (anonymous QR guests have no account/identity).
// Stores the unguessable checkout_token issued at check-in — never the sequential
// visitor_info id (that would be an enumerable IDOR on the public endpoints).
const SESSION_TTL_MS = 12 * 60 * 60 * 1000;
const sessionKey = (token) => `contractorCheckin:${token}`;

const readSession = (token) => {
  try {
    const raw = window.localStorage.getItem(sessionKey(token));
    if (!raw) {
      return null;
    }
    const data = JSON.parse(raw);
    // Require checkout_token; drop legacy sessions that only stored a numeric id.
    if (!data || !data.checkoutToken || !data.exp || data.exp < Date.now()) {
      window.localStorage.removeItem(sessionKey(token));
      return null;
    }
    return data;
  } catch (e) {
    return null;
  }
};

const writeSession = (token, checkoutToken, checkinTime) => {
  try {
    window.localStorage.setItem(
      sessionKey(token),
      JSON.stringify({ checkoutToken, checkinTime, exp: Date.now() + SESSION_TTL_MS })
    );
  } catch (e) {
    // Storage unavailable (private mode / disabled) — the checkout shortcut is a
    // nice-to-have, so a failure here must not break the check-in.
  }
};

const clearSession = (token) => {
  try {
    window.localStorage.removeItem(sessionKey(token));
  } catch (e) {
    // ignore
  }
};

// Best-effort device position. Resolves null on denial / unavailable / timeout so
// a check-in is never blocked by the location prompt.
const getPosition = () =>
  new Promise((resolve) => {
    if (!navigator.geolocation) {
      resolve(null);
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) =>
        resolve({ latitude: pos.coords.latitude, longitude: pos.coords.longitude }),
      () => resolve(null),
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 }
    );
  });

const formatTime = (value) => {
  if (!value) {
    return "";
  }
  const d = new Date(String(value).replace(" ", "T"));
  return isNaN(d.getTime()) ? value : d.toLocaleString();
};

const telHref = (phone) => `tel:${String(phone).replace(/[^\d+]/g, "")}`;

// Hub coordinates may arrive as strings (DECIMAL columns) — cast before use.
const hubMapsUrl = (hub) => {
  const lat = hub ? parseFloat(hub.latitude) : NaN;
  const lng = hub ? parseFloat(hub.longitude) : NaN;
  return Number.isFinite(lat) && Number.isFinite(lng)
    ? `https://www.google.com/maps?q=${lat},${lng}`
    : null;
};

// Contractor-side action on the "Before you start work" screen.
// sending: null | "confirm" | "fail"; errorKind says which call the error belongs to.
const CONTACT_ACTION_IDLE = { sending: null, error: "", errorKind: null, office: null };
// How often the screen re-reads division_contact.status while waiting.
const CONTACT_POLL_MS = 15000;

// Where to find the division contact, and the "make contact first" rule.
const DivisionContactCard = ({ division, office, beforeSubmit }) => {
  const hub = division.hub || null;
  const hubText = hub ? [hub.name, hub.note].filter(Boolean).join(" — ") : "";
  const mapsUrl = hubMapsUrl(hub);
  const contactName = division.contact_name;
  const phone = division.contact_phone;

  let instruction;
  if (!contactName) {
    instruction = "There is no contact listed for this area. Please go to the main office before starting work.";
  } else {
    const ways = [hubText ? `in person at ${hubText}` : "in person", phone ? "by phone" : null]
      .filter(Boolean)
      .join(" or ");
    instruction = `You must make contact with ${contactName} — ${ways} — before starting work. If you can't reach them, go to the main office.`;
  }

  return (
    <Card size="small" style={{ marginBottom: 16 }} title={division.name}>
      {contactName && (
        <p style={{ marginBottom: 4 }}>
          <strong>Contact:</strong> {contactName}
        </p>
      )}
      {phone && (
        <p style={{ marginBottom: 4 }}>
          <strong>Phone:</strong> <a href={telHref(phone)}>{phone}</a>
        </p>
      )}
      {hubText && (
        <p style={{ marginBottom: 4 }}>
          <strong>Hub:</strong> {hubText}
        </p>
      )}
      {mapsUrl && (
        <p style={{ marginBottom: 4 }}>
          <a href={mapsUrl} target="_blank" rel="noopener noreferrer">
            Open in Maps
          </a>
        </p>
      )}
      <Alert
        type="info"
        showIcon
        style={{ marginTop: 8 }}
        message={instruction}
        description={contactName ? null : <OfficeDetails office={office} />}
      />
      {beforeSubmit && contactName && (
        // On the info step the contact buttons are not there yet; say where they are.
        <p style={{ marginTop: 8, marginBottom: 0 }}>
          On the next screen you'll tell us whether you've made contact with {contactName}.
        </p>
      )}
    </Card>
  );
};

const officePeople = (office) =>
  (office && Array.isArray(office.people) ? office.people.filter((p) => p && (p.name || p.phone)) : []);

const hasOfficeDetails = (office) =>
  officePeople(office).length > 0 || !!(office && (office.name || office.phone || office.address));

// Large enough to tap first time on a phone.
const TAP_TARGET = { display: "inline-block", minHeight: 44, lineHeight: "44px", padding: "0 4px" };

// The nominated main-office people; the property's own contact details when
// none are nominated; and something to do when there is neither.
const OfficeDetails = ({ office }) => {
  const people = officePeople(office);
  if (people.length > 0) {
    return (
      <ul style={{ listStyle: "none", padding: 0, margin: "8px 0 0" }}>
        {people.map((person, i) => (
          <li key={`${person.name || ""}-${person.phone || ""}-${i}`} style={{ marginBottom: 4 }}>
            <strong>{person.name || "Main office"}</strong>
            {person.phone && (
              <>
                {" — "}
                <a href={telHref(person.phone)} style={TAP_TARGET}>{person.phone}</a>
              </>
            )}
          </li>
        ))}
      </ul>
    );
  }
  if (!hasOfficeDetails(office)) {
    return <p style={{ margin: "8px 0 0" }}>Ask at the main office or site entrance.</p>;
  }
  return (
    <div style={{ marginTop: 8 }}>
      {office.name && <p style={{ marginBottom: 4 }}><strong>{office.name}</strong></p>}
      {office.phone && (
        <p style={{ marginBottom: 4 }}>
          Phone: <a href={telHref(office.phone)} style={TAP_TARGET}>{office.phone}</a>
        </p>
      )}
      {office.address && <p style={{ marginBottom: 4 }}>{office.address}</p>}
    </div>
  );
};

export default function PropertyCheckinLanding() {
  const location = useLocation();
  const isLoggedIn = useSelector((state) => state.Auth.idToken);
  const [redirectToReferrer, setRedirectToReferrer] = useState(false);
  const landingToken = queryString.parse(window.location.search).token || "";
  // Per-division QR codes add &division=<id> to pre-select it for contractors.
  const presetDivisionId = queryString.parse(window.location.search).division || "";

  const [loading, setLoading] = useState(!!landingToken);
  const [landing, setLanding] = useState(null);
  const [step, setStep] = useState("role"); // role | info | precontact | form | contact | done
  const [roleLoading, setRoleLoading] = useState(false);
  const [formData, setFormData] = useState(null);
  const [property, setProperty] = useState(null);
  const [formToken, setFormToken] = useState("");
  const [visibleFormModal, setVisibleFormModal] = useState(false);
  const [pendingRole, setPendingRole] = useState(null);
  const [checkinInfo, setCheckinInfo] = useState(null);
  const [geo, setGeo] = useState(null);
  const [sessionModal, setSessionModal] = useState({ visible: false, checkinTime: null, checkoutToken: null });
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [checkedOut, setCheckedOut] = useState(false);
  // Division the contractor picked on the info step (drives the contact card).
  const [selectedDivisionId, setSelectedDivisionId] = useState(null);
  // After a contractor check-in with a division:
  // { division, checkoutToken, checkinTime, resumed } (division is null when resumed).
  const [postCheckin, setPostCheckin] = useState(null);
  // Server-side division_contact (status etc.); null until the server reports one.
  const [divisionContact, setDivisionContact] = useState(null);
  // The contractor's answer on "Before you start work", asked before the induction
  // form and sent with it: null | "made_contact" | "could_not_contact".
  const [preContact, setPreContact] = useState(null);
  const [contactAction, setContactAction] = useState(CONTACT_ACTION_IDLE);
  const [infoForm] = Form.useForm();
  // role=contractor (the Android fallback) skips the role screen.
  const isContractorEntry = queryString.parse(window.location.search).role === "contractor";
  const autoPickedRef = useRef(false);
  // pickRole is defined below the redirect early return; the effect reads it from
  // here so it gets the latest render's copy (with the loaded divisions).
  const pickRoleRef = useRef(null);

  const { from } = location.state || { from: { pathname: "/dashboard" } };

  useEffect(() => {
    if (isLoggedIn) {
      setRedirectToReferrer(true);
    }
  }, [isLoggedIn]);

  useEffect(() => {
    if (!landingToken) {
      setLoading(false);
      return;
    }
    setLoading(true);
    propertyCheckinService
      .getLanding(landingToken)
      .then((res) => {
        if (res && res.code === SUCCESS_CODE) {
          setLanding(res);
        } else {
          message.error((res && res.message) || "Invalid check-in link");
          setLanding(null);
        }
      })
      .catch(() => message.error("Failed to load property check-in"))
      .finally(() => setLoading(false));
  }, [landingToken]);

  useEffect(() => {
    if (redirectToReferrer || !isContractorEntry || autoPickedRef.current) {
      return;
    }
    if (landing && landing.has_contractor_form && pickRoleRef.current) {
      autoPickedRef.current = true;
      pickRoleRef.current("contractor");
    }
  }, [redirectToReferrer, isContractorEntry, landing]);

  // While "Before you start work" is on screen, re-read the contact status until
  // it is verified — after "failed" the main office can still confirm them.
  // Cleared on leaving the screen or unmount.
  const pollCheckoutToken = step === "contact" && postCheckin ? postCheckin.checkoutToken : null;
  const pollStatus = divisionContact ? divisionContact.status : null;
  useEffect(() => {
    if (!pollCheckoutToken || pollStatus === "verified") {
      return undefined;
    }
    let cancelled = false;
    const timer = setInterval(() => {
      propertyCheckinService
        .getContractorStatus(landingToken, pollCheckoutToken)
        .then((res) => {
          if (!cancelled && res && res.code === SUCCESS_CODE && res.division_contact) {
            setDivisionContact(res.division_contact);
          }
        })
        .catch(() => {
          // Transient network failure — the next tick tries again.
        });
    }, CONTACT_POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [landingToken, pollCheckoutToken, pollStatus]);

  if (redirectToReferrer) {
    return <Redirect to={from} />;
  }

  // Show the hard-coded Name/Phone/Email/Purpose screen for the chosen role.
  const goToInfo = (role) => {
    setCheckedOut(false);
    setPendingRole(role);
    setCheckinInfo(null);
    setGeo(null);
    setSelectedDivisionId(null);
    setPostCheckin(null);
    setDivisionContact(null);
    setPreContact(null);
    setContactAction(CONTACT_ACTION_IDLE);
    infoForm.resetFields();
    // Unknown ids from the QR are ignored silently.
    const preset = role === "contractor" && presetDivisionId
      ? divisions.find((d) => String(d.id) === String(presetDivisionId))
      : null;
    if (preset) {
      infoForm.setFieldsValue({ division_id: preset.id });
      setSelectedDivisionId(preset.id);
    }
    setStep("info");
  };

  // Load the role's check-in form inline after info is collected. For a
  // contractor, also kick off a best-effort position capture (non-blocking).
  const loadForm = (role) => {
    if (role === "contractor") {
      getPosition().then((coords) => {
        if (coords) {
          setGeo(coords);
        }
      });
    }
    setRoleLoading(true);
    propertyCheckinService
      .getForm(landingToken, role)
      .then((res) => {
        if (res && res.code === SUCCESS_CODE && res.form) {
          setFormData(res.form);
          setProperty(res.property || {});
          setFormToken(res.form.form_token || "");
          setVisibleFormModal(true);
          setStep("form");
        } else {
          message.error((res && res.message) || "Form is not available");
          setStep("role");
        }
      })
      .catch(() => {
        message.error("Failed to load check-in form");
        setStep("role");
      })
      .finally(() => setRoleLoading(false));
  };

  const pickRole = (role) => {
    // Contractor: if this browser already has an active check-in, offer check-out
    // or a fresh check-in instead of jumping straight into the info screen.
    if (role === "contractor") {
      const session = readSession(landingToken);
      if (session && session.checkoutToken) {
        setRoleLoading(true);
        propertyCheckinService
          .getContractorStatus(landingToken, session.checkoutToken)
          .then((res) => {
            const dc = res && res.division_contact;
            if (res && res.code === SUCCESS_CODE && res.checked_in && dc && dc.status !== "verified") {
              // Contact not confirmed yet — back to "Before you start work".
              setPostCheckin({
                division: null,
                checkoutToken: session.checkoutToken,
                checkinTime: res.checkin_time,
                resumed: true,
              });
              setDivisionContact(dc);
              setContactAction(CONTACT_ACTION_IDLE);
              setStep("contact");
            } else if (res && res.code === SUCCESS_CODE && res.checked_in) {
              setSessionModal({
                visible: true,
                checkinTime: res.checkin_time,
                checkoutToken: session.checkoutToken,
              });
            } else {
              clearSession(landingToken);
              goToInfo("contractor");
            }
          })
          .catch(() => {
            clearSession(landingToken);
            goToInfo("contractor");
          })
          .finally(() => setRoleLoading(false));
        return;
      }
    }
    goToInfo(role);
  };
  pickRoleRef.current = pickRole;

  // A contractor already checked in from this browser checks out here, so only
  // a fresh check-in is handed to the app.
  const pickContractor = () => {
    const contractorFormUrl = landing && landing.contractor_form_url;
    if (contractorFormUrl && isAndroid() && !isContractorEntry && !readSession(landingToken)) {
      openContractorApp(contractorFormUrl, landingToken, presetDivisionId);
      return;
    }
    pickRole("contractor");
  };

  const submitInfo = () => {
    infoForm
      .validateFields()
      .then((values) => {
        const info = {
          name: (values.name || "").trim(),
          phone: (values.phone || "").trim(),
          email: (values.email || "").trim(),
          purpose: (values.purpose || "").trim(),
          // Property-QR landing has the "already checked in -> check out" flow, so
          // it enables the contractor checkout_token (the per-form QR path does not).
          enable_checkout: true,
          // Contractors must supply a business name (Contractor-module matching key,
          // required server-side); visitors don't have this field.
          business_name: (values.business_name || "").trim(),
          visitor_type: pendingRole,
          // Only asked of contractors on properties that define divisions.
          division_id: showDivisions && values.division_id ? Number(values.division_id) : null,
        };
        setCheckinInfo(info);
        // A contractor with an area makes contact first, then does the induction form.
        if (info.division_id) {
          setPreContact(null);
          setStep("precontact");
        } else {
          loadForm(pendingRole);
        }
      })
      .catch(() => {
        // validation errors are shown inline by the form
      });
  };

  const doCheckout = () => {
    const checkoutToken = sessionModal.checkoutToken;
    setCheckoutLoading(true);
    getPosition()
      .then((coords) =>
        propertyCheckinService.contractorCheckout(landingToken, checkoutToken, coords || {})
      )
      .then((res) => {
        if (res && res.code === SUCCESS_CODE) {
          clearSession(landingToken);
          setSessionModal({ visible: false, checkinTime: null, checkoutToken: null });
          setPostCheckin(null);
          setDivisionContact(null);
          setStep("role");
          setCheckedOut(true);
          message.success("You have checked out");
        } else {
          message.error((res && res.message) || "Could not check out");
        }
      })
      .catch(() => message.error("Could not check out"))
      .finally(() => setCheckoutLoading(false));
  };

  const startNewCheckin = () => {
    clearSession(landingToken);
    setSessionModal({ visible: false, checkinTime: null, checkoutToken: null });
    goToInfo("contractor");
  };

  // Store the contractor session so a later scan can offer check-out.
  // With a division chosen, follow up with the "Before you start work" screen.
  const handleSubmitted = (res) => {
    if (pendingRole === "contractor" && res && res.checkout_token) {
      writeSession(landingToken, res.checkout_token, new Date().toISOString());
    }
    const division = pendingRole === "contractor" && checkinInfo && checkinInfo.division_id
      ? divisions.find((d) => Number(d.id) === Number(checkinInfo.division_id))
      : null;
    if (division) {
      setPostCheckin({
        division,
        checkoutToken: (res && res.checkout_token) || null,
        checkinTime: new Date().toISOString(),
        resumed: false,
      });
      // The submit response carries no status; start from what they answered
      // before the form (polling then takes over).
      const initialStatus = { made_contact: "contractor_confirmed", could_not_contact: "failed" }[checkinInfo.division_contact];
      setDivisionContact((res && res.division_contact) || (initialStatus ? { status: initialStatus } : null));
      setContactAction(CONTACT_ACTION_IDLE);
      setStep("contact");
    }
  };

  // Pre-form answer: "made_contact" goes straight on to the induction form;
  // "could_not_contact" first shows where the main office is.
  const answerPreContact = (answer) => {
    setPreContact(answer);
    setCheckinInfo((prev) => ({ ...prev, division_contact: answer, division_contact_at: Date.now() }));
    if (answer === "made_contact") {
      loadForm(pendingRole);
    }
  };

  // Changed their mind on the office screen — back to the two choices.
  const undoPreContact = () => {
    setPreContact(null);
    setCheckinInfo((prev) => {
      const { division_contact, division_contact_at, ...rest } = prev || {};
      return rest;
    });
  };

  // "I've made contact" (kind "confirm") or "I couldn't make contact" ("fail").
  // One request at a time; each is only offered while it still makes sense, so a
  // settled status cannot be re-sent. A failed request can be retried.
  const sendContactAction = (kind) => {
    const status = (divisionContact && divisionContact.status) || "awaiting";
    if (!postCheckin || !postCheckin.checkoutToken || contactAction.sending) {
      return;
    }
    if (kind === "confirm" ? status !== "awaiting" : (status === "verified" || status === "failed")) {
      return;
    }
    setContactAction((prev) => ({ ...prev, sending: kind, error: "", errorKind: null }));
    const call = kind === "confirm"
      ? propertyCheckinService.divisionContactConfirmed
      : propertyCheckinService.divisionContactFailed;
    call(landingToken, postCheckin.checkoutToken)
      .then((res) => {
        if (res && res.code === SUCCESS_CODE) {
          const fallbackStatus = kind === "confirm" ? "contractor_confirmed" : "failed";
          setDivisionContact((prev) => res.division_contact || { ...(prev || {}), status: fallbackStatus });
          setContactAction({ sending: null, error: "", errorKind: null, office: res.office || null });
        } else {
          setContactAction((prev) => ({
            ...prev,
            sending: null,
            errorKind: kind,
            error: (res && res.message)
              || (kind === "confirm" ? "We couldn't record that. Please try again." : "We could not alert staff."),
          }));
        }
      })
      .catch(() =>
        setContactAction((prev) => ({
          ...prev,
          sending: null,
          errorKind: kind,
          error: kind === "confirm" ? "We couldn't record that. Please try again." : "We could not alert staff.",
        }))
      );
  };

  const backToRole = () => {
    setStep("role");
    setVisibleFormModal(false);
    setFormData(null);
    setFormToken("");
    setProperty(null);
    setPendingRole(null);
    setCheckinInfo(null);
    setGeo(null);
    setSelectedDivisionId(null);
    setPostCheckin(null);
    setDivisionContact(null);
    setPreContact(null);
    setContactAction(CONTACT_ACTION_IDLE);
  };

  const propertyName =
    (landing && landing.property && landing.property.name) || "this property";
  const hasVisitor = !!(landing && landing.has_visitor_form);
  const hasContractor = !!(landing && landing.has_contractor_form);
  const noneConfigured = landing && !hasVisitor && !hasContractor;
  const roleLabel = pendingRole === "contractor" ? "Contractor" : "Visitor";
  const divisions = (landing && Array.isArray(landing.divisions)) ? landing.divisions : [];
  const showDivisions = pendingRole === "contractor" && divisions.length > 0;
  const selectedDivision = showDivisions && selectedDivisionId
    ? divisions.find((d) => Number(d.id) === Number(selectedDivisionId))
    : null;
  const landingOffice = (landing && landing.office) || null;
  const failedOffice = hasOfficeDetails(contactAction.office) ? contactAction.office : landingOffice;
  const contactStatus = (divisionContact && divisionContact.status) || "awaiting";
  // Server values win; the division picked at check-in fills any gaps.
  const dc = divisionContact || {};
  const pickedDivision = (postCheckin && postCheckin.division) || {};
  const cardDivision = {
    name: dc.division_name || pickedDivision.name,
    contact_name: dc.contact_name || pickedDivision.contact_name,
    contact_phone: dc.contact_phone || pickedDivision.contact_phone,
    hub: dc.hub || pickedDivision.hub || null,
  };
  const contactWho = cardDivision.contact_name || "your division contact";
  const canAct = !!(postCheckin && postCheckin.checkoutToken);

  return (
    <PropertyCheckinLandingWrapper className="isoSignInPage">
      <div className="isoLoginContentWrapper">
        <div className="isoLoginContent">
          <div className="isoLogoWrapper">
            <Link to="/dashboard">
              <img src={logo} height="40" alt="AirAgri" />
            </Link>
          </div>

          {loading && (
            <div style={{ textAlign: "center", padding: 40 }}>
              <Spin size="large" />
            </div>
          )}

          {!loading && !landingToken && (
            <Alert type="error" showIcon message="Missing check-in token" />
          )}

          {!loading && landingToken && !landing && (
            <Alert type="error" showIcon message="This check-in link is invalid or expired." />
          )}

          {!loading && landing && step === "role" && (
            <div className="property-checkin-role">
              <h1 className="welcome-title">Welcome to {propertyName}</h1>
              <p className="welcome-subtitle">
                Please tell us who you are so we can show the right check-in form.
              </p>

              {checkedOut && (
                <Alert
                  type="success"
                  showIcon
                  style={{ marginBottom: 16 }}
                  message="You have checked out. Thanks for visiting."
                />
              )}

              {noneConfigured && (
                <Alert
                  type="warning"
                  showIcon
                  style={{ marginBottom: 16 }}
                  message="Check-in is not configured for this property yet."
                />
              )}

              <Button
                type="primary"
                className="btn-success role-button"
                size="large"
                block
                disabled={!hasVisitor || roleLoading}
                loading={roleLoading}
                onClick={() => pickRole("visitor")}
              >
                I am a Visitor
              </Button>
              {!hasVisitor && !noneConfigured && (
                <p className="role-hint">Visitor check-in is not configured for this property.</p>
              )}

              <Button
                type="primary"
                className="btn-success role-button"
                size="large"
                block
                disabled={!hasContractor || roleLoading}
                loading={roleLoading}
                onClick={pickContractor}
                style={{ marginTop: 12 }}
              >
                I am a Contractor
              </Button>
              {!hasContractor && !noneConfigured && (
                <p className="role-hint">Contractor check-in is not configured for this property.</p>
              )}
            </div>
          )}

          {!loading && landing && step === "info" && (
            <div className="property-checkin-info">
              <Button type="link" onClick={() => setStep("role")} style={{ paddingLeft: 0, marginBottom: 8 }}>
                ← Back
              </Button>
              <h2 className="welcome-title">{roleLabel} check-in</h2>
              <p className="welcome-subtitle">Please provide your details to check in.</p>
              <Form form={infoForm} layout="vertical" onFinish={submitInfo}>
                <Form.Item
                  name="name"
                  label="Name"
                  rules={[{ required: true, whitespace: true, message: "Please enter your name" }]}
                >
                  <Input size="large" placeholder="Full name" />
                </Form.Item>
                <Form.Item
                  name="phone"
                  label="Phone Number"
                  rules={[{ required: true, whitespace: true, message: "Please enter your phone number" }]}
                >
                  <Input size="large" placeholder="Phone number" />
                </Form.Item>
                <Form.Item
                  name="email"
                  label="Email"
                  rules={[
                    { required: true, message: "Please enter your email" },
                    { type: "email", message: "Please enter a valid email" },
                  ]}
                >
                  <Input size="large" placeholder="Email" />
                </Form.Item>
                {pendingRole === "contractor" && (
                  <Form.Item
                    name="business_name"
                    label="Business / Company Name"
                    rules={[{ required: true, whitespace: true, message: "Please enter your business name" }]}
                  >
                    <Input size="large" placeholder="Business or company name" />
                  </Form.Item>
                )}
                {showDivisions && (
                  <Form.Item
                    name="division_id"
                    label="Area you are attending"
                    rules={[{ required: true, message: "Please choose the area you are attending" }]}
                  >
                    <Select
                      size="large"
                      placeholder="Choose an area"
                      onChange={(value) => setSelectedDivisionId(value)}
                      options={divisions.map((d) => ({ value: d.id, label: d.name }))}
                    />
                  </Form.Item>
                )}
                {selectedDivision && <DivisionContactCard division={selectedDivision} office={landingOffice} beforeSubmit />}
                <Form.Item name="purpose" label="Purpose of Visit">
                  <Input size="large" placeholder="Purpose of visit (optional)" />
                </Form.Item>
                <Button
                  type="primary"
                  htmlType="submit"
                  className="btn-success role-button"
                  size="large"
                  block
                  loading={roleLoading}
                >
                  Continue
                </Button>
              </Form>
            </div>
          )}

          {!loading && landing && step === "precontact" && selectedDivision && (
            <div className="property-checkin-contact">
              <Button type="link" onClick={() => setStep("info")} style={{ paddingLeft: 0, marginBottom: 8 }}>
                ← Back
              </Button>
              <h2 className="welcome-title">Before you start work</h2>

              {preContact !== "could_not_contact" && (
                <>
                  <DivisionContactCard division={selectedDivision} office={landingOffice} />
                  {selectedDivision.contact_name ? (
                    <>
                      <Button
                        type="primary"
                        className="btn-success role-button"
                        size="large"
                        block
                        loading={roleLoading}
                        onClick={() => answerPreContact("made_contact")}
                      >
                        I've made contact
                      </Button>
                      <Button
                        danger
                        size="large"
                        block
                        style={{ marginTop: 12 }}
                        disabled={roleLoading}
                        onClick={() => answerPreContact("could_not_contact")}
                      >
                        I couldn't make contact
                      </Button>
                    </>
                  ) : (
                    <Button
                      type="primary"
                      className="btn-success role-button"
                      size="large"
                      block
                      loading={roleLoading}
                      onClick={() => loadForm(pendingRole)}
                    >
                      Continue to sign-in
                    </Button>
                  )}
                </>
              )}

              {preContact === "could_not_contact" && (
                <>
                  <Alert
                    type="warning"
                    showIcon
                    style={{ marginBottom: 16 }}
                    message="Please go to the main office"
                    description={
                      <>
                        <OfficeDetails office={landingOffice} />
                        <p style={{ margin: "8px 0 0" }}>
                          Finish signing in first: staff are told you couldn't reach{" "}
                          {selectedDivision.contact_name} as soon as you submit, and the main office
                          can then confirm you.
                        </p>
                      </>
                    }
                  />
                  <Button
                    type="primary"
                    className="btn-success role-button"
                    size="large"
                    block
                    loading={roleLoading}
                    onClick={() => loadForm(pendingRole)}
                  >
                    Continue to sign-in
                  </Button>
                  <Button type="link" block style={{ marginTop: 12 }} disabled={roleLoading} onClick={undoPreContact}>
                    I've made contact after all
                  </Button>
                </>
              )}
            </div>
          )}

          {!loading && landing && step === "form" && (
            <div className="property-checkin-form-step">
              <Button type="link" onClick={backToRole} style={{ paddingLeft: 0, marginBottom: 8 }}>
                ← Back
              </Button>
              <p className="form-property-name">{property && property.name ? property.name : propertyName}</p>
            </div>
          )}

          {!loading && landing && step === "contact" && postCheckin && (
            <div className="property-checkin-contact">
              <h2 className="welcome-title">Before you start work</h2>

              {/* Polled status — announced to screen readers when it changes. */}
              <div role="status" aria-live="polite">
                {contactStatus === "awaiting" && (
                  <Alert
                    type="warning"
                    showIcon
                    style={{ marginBottom: 16 }}
                    message={`Waiting for ${contactWho} to confirm you've made contact. Don't start work until they confirm.`}
                  />
                )}
                {contactStatus === "contractor_confirmed" && (
                  <Alert
                    type="info"
                    showIcon
                    style={{ marginBottom: 16 }}
                    message={`You've told us you made contact — waiting for ${contactWho} to confirm.`}
                  />
                )}
                {contactStatus === "verified" && (
                  <Result
                    status="success"
                    title="Contact confirmed"
                    subTitle={`Contact confirmed by ${dc.verified_by_name || contactWho}${dc.verified_at ? ` at ${formatTime(dc.verified_at)}` : ""}. You can start work.`}
                  />
                )}
                {contactStatus === "failed" && (
                  <Alert
                    type="warning"
                    showIcon
                    style={{ marginBottom: 16 }}
                    message="Please go to the main office"
                    description={
                      <>
                        <p style={{ marginBottom: 4 }}>Staff have been told you couldn't make contact.</p>
                        <OfficeDetails office={failedOffice} />
                        <p style={{ margin: "8px 0 0" }}>Once the main office confirms you, this screen will update.</p>
                      </>
                    }
                  />
                )}
              </div>

              {contactStatus !== "verified" && contactStatus !== "failed" && (
                <DivisionContactCard division={cardDivision} office={landingOffice} />
              )}

              {contactAction.error && (
                <Alert
                  type="error"
                  showIcon
                  style={{ marginBottom: 16 }}
                  message={contactAction.error}
                  description={contactAction.errorKind === "fail" ? (
                    <>
                      <p style={{ marginBottom: 4 }}>Please go to the main office.</p>
                      <OfficeDetails office={landingOffice} />
                    </>
                  ) : null}
                />
              )}

              {!canAct && contactStatus !== "verified" && (
                <Alert
                  type="info"
                  style={{ marginBottom: 16 }}
                  message="If you can't make contact, go to the main office."
                  description={<OfficeDetails office={landingOffice} />}
                />
              )}

              {canAct && contactStatus === "awaiting" && (
                <Button
                  type="primary"
                  className="btn-success role-button"
                  size="large"
                  block
                  loading={contactAction.sending === "confirm"}
                  disabled={!!contactAction.sending}
                  onClick={() => sendContactAction("confirm")}
                >
                  I've made contact
                </Button>
              )}
              {canAct && (contactStatus === "awaiting" || contactStatus === "contractor_confirmed") && (
                <Button
                  danger
                  size="large"
                  block
                  style={{ marginTop: 12 }}
                  loading={contactAction.sending === "fail"}
                  disabled={!!contactAction.sending}
                  onClick={() => sendContactAction("fail")}
                >
                  I couldn't make contact
                </Button>
              )}
              {(contactStatus === "verified" || contactStatus === "failed" || !canAct) && (
                <Button
                  type="primary"
                  className="btn-success role-button"
                  size="large"
                  block
                  onClick={() => setStep("done")}
                >
                  Done
                </Button>
              )}
              {postCheckin.resumed && (
                <Button
                  type="link"
                  block
                  style={{ marginTop: 12 }}
                  onClick={() => setSessionModal({
                    visible: true,
                    checkinTime: postCheckin.checkinTime,
                    checkoutToken: postCheckin.checkoutToken,
                  })}
                >
                  Check out or start a new check-in
                </Button>
              )}
            </div>
          )}

          {!loading && landing && step === "done" && (
            <Result
              status="success"
              title="You're checked in"
              subTitle={`Thanks — have a safe day at ${propertyName}.`}
              extra={
                <Button type="link" onClick={backToRole}>
                  Back to start
                </Button>
              }
            />
          )}
        </div>
      </div>

      <VisitorFormModal
        visibleFormModal={visibleFormModal && step === "form"}
        cancelModal={backToRole}
        propertyId={formData && formData.property_id}
        formData={formData || {}}
        property={property || {}}
        formToken={formToken}
        visitorInfo={checkinInfo}
        extraFormData={geo || {}}
        onSubmitted={handleSubmitted}
        hideCancel
        simpleFinal
      />

      <Modal
        visible={sessionModal.visible}
        title="You are already checked in"
        onCancel={() => setSessionModal({ visible: false, checkinTime: null, checkoutToken: null })}
        maskClosable={!checkoutLoading}
        footer={[
          <Button key="new" onClick={startNewCheckin} disabled={checkoutLoading}>
            Start new check-in
          </Button>,
          <Button
            key="checkout"
            type="primary"
            className="btn-success"
            loading={checkoutLoading}
            onClick={doCheckout}
          >
            Check out now
          </Button>,
        ]}
      >
        <p>
          You checked in at <strong>{formatTime(sessionModal.checkinTime)}</strong>.
        </p>
        <p>Do you want to check out now, or start a new check-in?</p>
      </Modal>
    </PropertyCheckinLandingWrapper>
  );
}
